import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Upload, Menu, X, LogOut, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { NAV_CATEGORIES } from "@/lib/categories";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function ShopMegaMenu() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="nav-shop"
          className="font-micro text-[#D8D6D1] hover:text-[#B8895C] transition-colors duration-500 flex items-center gap-1.5 outline-none"
        >
          Shop <ChevronDown size={12} strokeWidth={1.5} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        data-testid="nav-shop-menu"
        className="min-w-[820px] rounded-none border-[#2A2825] bg-[#141414] text-[#EDE4D3] p-8 mt-2"
      >
        <div className="grid grid-cols-3 gap-x-8 gap-y-6">
          {NAV_CATEGORIES.map((c) => (
            <div key={c.slug} data-testid={`nav-shop-cat-${c.slug}`}>
              <Link
                to={`/category/${c.slug}`}
                className="font-serif text-[#EDE4D3] text-lg block mb-2 hover:text-[#B8895C] transition-colors duration-500"
                data-testid={`nav-shop-link-${c.slug}`}
              >
                {c.name}
              </Link>
              <ul className="space-y-1.5">
                {c.sub_categories.map((s) => (
                  <li key={s}>
                    <Link
                      to={`/category/${c.slug}?sub=${encodeURIComponent(s)}`}
                      className="font-micro text-[#8A8680] hover:text-[#B8895C] transition-colors duration-500"
                      data-testid={`nav-shop-sub-${c.slug}-${s.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {s}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-8 pt-6 flex justify-between items-center" style={{ borderTop: "1px solid var(--hairline)" }}>
          <div className="font-micro text-[#8A8680]">Every piece, made to order.</div>
          <Link to="/custom-design" className="font-micro text-[#B8895C] link-underline" data-testid="nav-shop-customise">
            Customise a Piece →
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Nav() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openCat, setOpenCat] = useState(null); // mobile accordion

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      data-testid="site-nav"
      className={`sticky top-0 z-50 transition-colors duration-700 ${scrolled ? "backdrop-blur-md bg-[#0B0B0C]/85" : "bg-transparent"}`}
      style={{ borderBottom: "1px solid var(--hairline)" }}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-5 grid grid-cols-3 items-center">
        <div className="hidden md:flex gap-8 items-center">
          <ShopMegaMenu />
          <Link to="/custom-design" className="font-micro link-underline text-[#D8D6D1]" data-testid="nav-customise">Customise</Link>
          <Link to="/category/swatches" className="font-micro link-underline text-[#D8D6D1]" data-testid="nav-swatches">Swatches</Link>
          <Link to="/about" className="font-micro link-underline text-[#D8D6D1]" data-testid="nav-about">About</Link>
        </div>

        <button className="md:hidden justify-self-start text-[#EDE4D3]" onClick={() => setOpen(true)} data-testid="nav-open-mobile">
          <Menu size={22} strokeWidth={1} />
        </button>

        <Link to="/" className="justify-self-center text-center" data-testid="nav-logo">
          <div className="font-serif text-[#EDE4D3] text-2xl md:text-3xl tracking-tight leading-none">The Teak Studio</div>
          <div className="font-micro text-[#8A8680] mt-1">Direct from Factory</div>
        </Link>

        <div className="justify-self-end flex items-center gap-6">
          <Link to="/custom-design" className="hidden md:inline-flex text-[#EDE4D3] hover:text-[#B8895C] transition-colors duration-500" title="Customise a Piece" data-testid="nav-custom-design">
            <Upload size={18} strokeWidth={1.25} />
          </Link>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="text-[#EDE4D3] hover:text-[#B8895C] transition-colors duration-500 outline-none"
                  title={user.name}
                  data-testid="nav-account"
                >
                  <User size={18} strokeWidth={1.25} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                data-testid="nav-account-menu"
                className="min-w-[220px] rounded-none border-[#2A2825] bg-[#141414] text-[#EDE4D3] p-0"
              >
                <DropdownMenuLabel className="px-4 py-3 border-b border-[#2A2825]">
                  <div className="font-micro text-[#8A8680] mb-1">Signed in as</div>
                  <div className="font-serif text-[#EDE4D3] text-lg leading-none" data-testid="nav-account-name">{user.name}</div>
                  <div className="font-micro text-[#8A8680] mt-2 text-[0.65rem] normal-case tracking-normal">{user.email}</div>
                </DropdownMenuLabel>
                {user.role === "admin" && (
                  <DropdownMenuItem
                    className="px-4 py-3 font-micro cursor-pointer text-[#B8895C] focus:bg-[#0B0B0C] focus:text-[#B8895C] rounded-none"
                    onSelect={() => nav("/admin")}
                    data-testid="nav-account-admin"
                  >
                    Admin Console
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="px-4 py-3 font-micro cursor-pointer text-[#D8D6D1] focus:bg-[#0B0B0C] focus:text-[#B8895C] rounded-none"
                  onSelect={() => nav("/cart")}
                  data-testid="nav-account-cart"
                >
                  My Cart
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#2A2825] m-0" />
                <DropdownMenuItem
                  className="px-4 py-3 font-micro cursor-pointer text-[#D8D6D1] focus:bg-[#0B0B0C] focus:text-[#B8895C] rounded-none"
                  onSelect={async () => { await logout(); nav("/"); }}
                  data-testid="nav-account-logout"
                >
                  <LogOut size={14} strokeWidth={1.25} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/login"
              className="text-[#EDE4D3] hover:text-[#B8895C] transition-colors duration-500"
              title="Sign in"
              data-testid="nav-account"
            >
              <User size={18} strokeWidth={1.25} />
            </Link>
          )}
          <Link to="/cart" className="text-[#EDE4D3] hover:text-[#B8895C] transition-colors duration-500 relative" data-testid="nav-cart">
            <ShoppingBag size={18} strokeWidth={1.25} />
            {count > 0 && (
              <span className="absolute -top-2 -right-3 bg-[#B8895C] text-[#0B0B0C] text-[10px] w-4 h-4 flex items-center justify-center font-medium">
                {count}
              </span>
            )}
          </Link>
        </div>
      </div>

      {open && (
        <div className="md:hidden fixed inset-0 bg-[#0B0B0C] z-50 fade-in overflow-y-auto" data-testid="nav-mobile-menu">
          <div className="p-6 flex justify-between items-center sticky top-0 bg-[#0B0B0C] z-10" style={{ borderBottom: "1px solid var(--hairline)" }}>
            <div className="font-serif text-[#EDE4D3] text-xl">The Teak Studio</div>
            <button onClick={() => setOpen(false)} className="text-[#EDE4D3]" data-testid="nav-close-mobile"><X size={22} strokeWidth={1} /></button>
          </div>
          <nav className="p-6 flex flex-col divide-y divide-[#2A2825]">
            {NAV_CATEGORIES.map((c) => (
              <div key={c.slug} className="py-4">
                <button
                  onClick={() => setOpenCat(openCat === c.slug ? null : c.slug)}
                  className="w-full flex justify-between items-center text-left"
                  data-testid={`nav-mobile-cat-${c.slug}`}
                >
                  <span className="font-serif text-[#EDE4D3] text-2xl">{c.name}</span>
                  <ChevronDown
                    size={16}
                    strokeWidth={1.5}
                    className={`text-[#8A8680] transition-transform duration-500 ${openCat === c.slug ? "rotate-180 text-[#B8895C]" : ""}`}
                  />
                </button>
                {openCat === c.slug && (
                  <div className="pt-4 pl-2 space-y-3 fade-in">
                    <Link to={`/category/${c.slug}`} onClick={() => setOpen(false)} className="font-micro text-[#B8895C] link-underline block" data-testid={`nav-mobile-all-${c.slug}`}>
                      All {c.name}
                    </Link>
                    {c.sub_categories.map((s) => (
                      <Link
                        key={s}
                        to={`/category/${c.slug}?sub=${encodeURIComponent(s)}`}
                        onClick={() => setOpen(false)}
                        className="font-micro text-[#8A8680] hover:text-[#B8895C] block"
                        data-testid={`nav-mobile-sub-${c.slug}-${s.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        {s}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="py-4">
              <Link to="/about" onClick={() => setOpen(false)} className="font-serif text-[#EDE4D3] text-2xl" data-testid="nav-mobile-about">About</Link>
            </div>
            <div className="py-4">
              <Link to="/custom-design" onClick={() => setOpen(false)} className="font-serif text-[#B8895C] text-2xl" data-testid="nav-mobile-custom">Customise a Piece</Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
